import { z } from 'zod';
import { agencyTaskPrioritySchema } from './list';

export const AGENCY_TASK_RECURRENCE_FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'] as const;
export const agencyTaskRecurrenceFrequencySchema = z.enum(AGENCY_TASK_RECURRENCE_FREQUENCIES);
export type AgencyTaskRecurrenceFrequency = z.infer<typeof agencyTaskRecurrenceFrequencySchema>;

// Exactly one end condition (or none) — a discriminated union instead of two
// loosely-optional endDate/maxOccurrences fields so an invalid combination
// (both set, or neither with an implicit "never") can't be represented.
export const agencyTaskRecurrenceEndSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('never') }),
  z.object({ type: z.literal('onDate'), date: z.coerce.date() }),
  z.object({ type: z.literal('afterCount'), count: z.number().int().min(1) }),
]);
export type AgencyTaskRecurrenceEnd = z.infer<typeof agencyTaskRecurrenceEndSchema>;

// 0=Sunday .. 6=Saturday, matching JS Date#getDay().
const dayOfWeekSchema = z.number().int().min(0).max(6);

export const agencyTaskRecurrenceRuleSchema = z.discriminatedUnion('frequency', [
  z.object({
    frequency: z.literal('daily'),
    interval: z.number().int().min(1),
    end: agencyTaskRecurrenceEndSchema,
  }),
  z.object({
    frequency: z.literal('weekly'),
    interval: z.number().int().min(1),
    daysOfWeek: z.array(dayOfWeekSchema).min(1),
    end: agencyTaskRecurrenceEndSchema,
  }),
  z.object({
    frequency: z.literal('monthly'),
    interval: z.number().int().min(1),
    // Clamped to a given month's actual last day when it doesn't have this many days.
    dayOfMonth: z.number().int().min(1).max(31),
    end: agencyTaskRecurrenceEndSchema,
  }),
  z.object({
    frequency: z.literal('yearly'),
    interval: z.number().int().min(1),
    end: agencyTaskRecurrenceEndSchema,
  }),
]);
export type AgencyTaskRecurrenceRule = z.infer<typeof agencyTaskRecurrenceRuleSchema>;

export const agencyTaskRecurrenceSchema = z.object({
  id: z.string(),
  agencyId: z.string(),
  clientId: z.string().nullable().optional(),
  createdByUserId: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  priority: agencyTaskPrioritySchema,
  userId: z.string().nullable().optional(),
  assignedToClient: z.boolean().optional(),
  isPrivate: z.boolean().optional(),
  rule: agencyTaskRecurrenceRuleSchema,
  startDate: z.coerce.date(),
  lastOccurrenceDueDate: z.coerce.date().nullable().optional(),
  occurrencesGenerated: z.number(),
  endedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type AgencyTaskRecurrenceSchema = z.infer<typeof agencyTaskRecurrenceSchema>;

export const createAgencyTaskRecurrenceSchema = z.object({
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  clientId: z.string().nullable().optional(),
  userId: z.string().nullable().optional(),
  assignedToClient: z.boolean().optional(),
  isPrivate: z.boolean().optional(),
  priority: agencyTaskPrioritySchema.optional(),
  startDate: z.coerce.date(),
  rule: agencyTaskRecurrenceRuleSchema,
});
export type CreateAgencyTaskRecurrenceSchema = z.infer<typeof createAgencyTaskRecurrenceSchema>;

export const updateAgencyTaskRecurrenceSchema = createAgencyTaskRecurrenceSchema.partial();
export type UpdateAgencyTaskRecurrenceSchema = z.infer<typeof updateAgencyTaskRecurrenceSchema>;
